import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
// Importiere Ant Design Komponenten
import { Card, Radio, Button, Typography, List, Space, Tag } from 'antd';
import { CheckCircleOutlined, StarFilled } from '@ant-design/icons';

const { Title, Text, Paragraph } = Typography;

// Plan-Daten (könnten auch von außen kommen)
const plansData = {
    premium: {
        id: 'premium',
        name: 'Premium',
        prices: { monthly: 19.99, yearly: 199.99 }, // Beispiel für jährlichen Rabatt
        features: [
            'Bilder hochladen',
            'Statistiken einsehen',
            'Öffnungszeiten hinzufügen',
            'Inhalte bearbeiten',
            'Verifiziert-Badge'
        ],
        extraFeatures: [],
        color: '#1890ff' // Basisfarbe
    },
    professional: {
        id: 'professional',
        name: 'Professional',
        prices: { monthly: 29.99, yearly: 299.99 }, // Beispiel für jährlichen Rabatt
        features: [
            'Bilder hochladen',
            'Statistiken einsehen',
            'Öffnungszeiten hinzufügen',
            'Inhalte bearbeiten',
            'Verifiziert-Badge'
        ],
        extraFeatures: [
            'Premium Support',
            'Hervorgehobene Shoplistings (Homepage, Rahmen, Stern)'
        ],
        color: '#faad14' // Ant Design Gold Farbton
    }
};

const NewPricingPlans = ({ onSelect, currentPlanId, currentInterval }) => {
    // State für das *global* ausgewählte Intervall, da Radio.Group besser dafür geeignet ist
    const [selectedInterval, setSelectedInterval] = useState(currentInterval || 'monthly');
    // Neuer State, um die vom Benutzer in *dieser* Komponente ausgewählte Karte zu tracken
    const [activelySelectedPlanId, setActivelySelectedPlanId] = useState(null);

    // Beim Ändern des Intervalls die aktive Auswahl zurücksetzen?
    useEffect(() => {
        setActivelySelectedPlanId(null); // Auswahl aufheben, wenn Intervall wechselt
    }, [selectedInterval]);

    const handleSelectPlan = (planId) => {
        // Setze die aktiv ausgewählte Karte
        setActivelySelectedPlanId(planId);
        // Rufe den Callback der Elternkomponente auf
        if (onSelect) {
            onSelect({ id: planId, interval: selectedInterval });
        }
    };

    return (
        <div className="antd-pricing-plans-container" style={{ padding: '20px 0' }}>
            {/* Globaler Intervall-Schalter oben */} 
            <div style={{ textAlign: 'center', marginBottom: '30px' }}>
                <Radio.Group 
                    value={selectedInterval} 
                    onChange={(e) => setSelectedInterval(e.target.value)}
                    optionType="button"
                    buttonStyle="solid"
                    size="large" // Größere Buttons
                >
                    <Radio.Button value="monthly">Monatlich</Radio.Button>
                    <Radio.Button value="yearly">Jährlich (sparen)</Radio.Button>
                </Radio.Group>
            </div>

            {/* justify: 'center' sorgt dafür, dass die Karten zentriert werden, wenn sie umbrechen */}
            <Space align="stretch" size="large" wrap style={{ justifyContent: 'center', display: 'flex' }}>
                {Object.values(plansData).map(plan => {
                    const price = plan.prices[selectedInterval];
                    const isCurrentPlan = plan.id === currentPlanId;
                    const isProfessional = plan.id === 'professional';
                    // Ist diese Karte die aktuell ausgewählte?
                    const isActivelySelected = plan.id === activelySelectedPlanId;

                    return (
                        <Card 
                            key={plan.id} 
                            hoverable={!isCurrentPlan} 
                            title={
                                <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Title level={3} style={{ marginBottom: 0, marginRight: isProfessional ? '8px' : 0 }}>
                                        {plan.name}
                                    </Title>
                                    {isProfessional && <Tag color={plan.color} icon={<StarFilled />} style={{ fontSize: '0.9em', fontWeight: 'bold' }}>Top</Tag>}
                                </div>
                            } 
                            bordered={true}
                            style={{
                                width: 330,
                                boxShadow: isActivelySelected 
                                    ? `0 6px 16px ${plan.color}40` // Hervorgehobener Schatten bei Auswahl
                                    : '0 4px 12px rgba(0,0,0,0.08)',
                                border: isActivelySelected 
                                    ? `2px solid ${plan.color}` // Dickerer Rand bei Auswahl
                                    : '1px solid #f0f0f0', // Standard-Rand leicht sichtbar machen
                                borderTop: `5px solid ${plan.color}`,
                                display: 'flex',       
                                flexDirection: 'column',
                                transition: 'all 0.2s ease-in-out',
                                cursor: isCurrentPlan ? 'default' : 'pointer',
                                // Leicht verkleinern, wenn nicht ausgewählt, um Auswahl zu betonen
                                transform: isActivelySelected ? 'scale(1.02)' : 'scale(1)', 
                            }}
                            // Hover-Effekt nur für auswählbare Karten, die NICHT aktiv ausgewählt sind
                            onMouseEnter={(e) => { if (!isCurrentPlan && !isActivelySelected) { e.currentTarget.style.transform = 'scale(1.03)'; e.currentTarget.style.boxShadow = '0 8px 20px rgba(0,0,0,0.15)'; }}}
                            onMouseLeave={(e) => { if (!isCurrentPlan && !isActivelySelected) { e.currentTarget.style.transform = 'scale(1)'; e.currentTarget.style.boxShadow = '0 4px 12px rgba(0,0,0,0.08)'; }}}
                            onClick={() => !isCurrentPlan && handleSelectPlan(plan.id)} 
                            headStyle={{ borderBottom: '1px solid #f0f0f0', paddingTop: '20px', paddingBottom: '10px', textAlign: 'center' }}
                            bodyStyle={{ 
                                flexGrow: 1,              
                                display: 'flex',          
                                flexDirection: 'column',
                                padding: '20px 25px 25px 25px' 
                            }}
                        >
                            {/* Preisanzeige zentriert */}
                            <div style={{ textAlign: 'center', marginBottom: '25px' }}>
                                <Text style={{ fontSize: '2.8em', fontWeight: '600', color: plan.color }}>
                                    €{price.toFixed(2)}
                                </Text>
                                <Text type="secondary" style={{ display: 'block', marginTop: '-5px' }}>
                                    pro {selectedInterval === 'monthly' ? 'Monat' : 'Jahr'}
                                </Text>
                            </div>

                            {/* Feature-Liste */}
                            <List
                                itemLayout="horizontal"
                                dataSource={[...plan.features, ...plan.extraFeatures]}
                                renderItem={(item, index) => (
                                    <List.Item style={{ borderBottom: 'none', padding: '7px 0' }}> {/* Etwas mehr Abstand */}
                                        <CheckCircleOutlined style={{ color: '#52c41a', marginRight: '10px', fontSize: '1.1em' }} /> 
                                        <Text style={{ fontWeight: index >= plan.features.length ? '600' : 'normal' }}>
                                            {item}
                                        </Text>
                                    </List.Item>
                                )}
                                style={{ 
                                    marginBottom: '25px',
                                    flexGrow: 1
                                }}
                            />
                            
                            <Button 
                                type="primary" 
                                block 
                                size="large" 
                                disabled={isCurrentPlan}
                                // Button hervorheben, wenn Karte aktiv ausgewählt ist
                                ghost={!isActivelySelected && !isCurrentPlan} 
                                style={{ 
                                    fontWeight: '600',
                                    // Farbe nur setzen, wenn nicht Ghost
                                    backgroundColor: isActivelySelected ? plan.color : (isCurrentPlan ? undefined : 'transparent'), 
                                    borderColor: isActivelySelected ? plan.color : (isCurrentPlan ? undefined : plan.color),
                                    // Textfarbe anpassen
                                    color: isActivelySelected ? '#fff' : (isCurrentPlan ? undefined : plan.color), 
                                    opacity: isCurrentPlan ? 0.65 : 1,
                                }}
                                icon={isProfessional ? <StarFilled /> : null}
                            >
                                {isCurrentPlan ? 'Aktueller Plan' : 
                                 isActivelySelected ? 'Ausgewählt' : `${plan.name} wählen`}
                            </Button>
                        </Card>
                    );
                })}
            </Space>
        </div>
    );
};

NewPricingPlans.propTypes = {
    onSelect: PropTypes.func.isRequired,
    currentPlanId: PropTypes.string,
    currentInterval: PropTypes.string,
};

export default NewPricingPlans; 